/**
 * TanStack Query hooks for Organizations
 *
 * This file provides React Query hooks for fetching and mutating organization data.
 * These hooks replace the async methods in the Zustand organizations store.
 *
 * @module organizations-queries
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Organization } from '../../entity/api/organization';
import type { SaveOrganizationReq } from '../../entity/api/routes/organization';
import {
  getOrganizationById,
  getOrganizationsList,
  updateOrganization,
} from '../../sdk/organization';
import { organizationUsersKeys } from './organization-users-queries';

// ============ QUERY KEYS ============

export const organizationsKeys = {
  all: ['organizations'] as const,
  list: function () {
    return [...organizationsKeys.all, 'list'] as const;
  },
  byId: function (id: string) {
    return [...organizationsKeys.all, id] as const;
  },
};

// ============ QUERIES ============

/**
 * Fetch the list of organizations for the current user
 *
 * Replaces: getStores().organizations.getState().list()
 */
export function useOrganizationsList() {
  return useQuery({
    queryKey: organizationsKeys.list(),
    queryFn: async function () {
      const response = await getOrganizationsList();
      return response.data.organizations;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export const ORGANIZATION_BY_ID_STALE_MS = 1000 * 60 * 5; // 5 minutes

/** Shared queryFn for organization by id (used by hook and prefetch for deduplication) */
export async function organizationByIdQueryFn(id: string): Promise<Organization> {
  const response = await getOrganizationById(id);
  return response.data;
}

/**
 * Fetch a single organization by ID
 *
 * Replaces: getStores().organizations.getState().getById(id)
 */
export function useOrganizationById(id: string | undefined | null) {
  return useQuery({
    queryKey: organizationsKeys.byId(id ?? ''),
    queryFn: () => organizationByIdQueryFn(id!),
    enabled: id != null && id !== '',
    staleTime: ORGANIZATION_BY_ID_STALE_MS,
  });
}

/**
 * Prefetch organization by id and cache it (same query as useOrganizationById).
 * Use in route beforeLoad to avoid duplicate GET when layout/tabs mount.
 */
export async function prefetchOrganizationById(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: organizationsKeys.byId(id),
    queryFn: () => organizationByIdQueryFn(id),
    staleTime: ORGANIZATION_BY_ID_STALE_MS,
  });
}

// ============ MUTATIONS ============

/**
 * Update an organization
 *
 * Replaces: getStores().organizations.getState().update(id, data)
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id, data }: { id: string; data: SaveOrganizationReq['body'] }) {
      const response = await updateOrganization(id, data);
      return response;
    },
    onSuccess: function (data, { id }) {
      // Update the single organization cache
      queryClient.setQueryData(organizationsKeys.byId(id), data);

      // Update the organization in the list cache
      queryClient.setQueryData<Organization[]>(organizationsKeys.list(), function (oldList) {
        if (oldList == null) {
          return oldList;
        }
        return oldList.map(function (org) {
          if (org._id === id) {
            return data;
          }
          return org;
        });
      });
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get organization from cache by ID (imperative access)
 */
export function getOrganizationFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Organization | undefined {
  return queryClient.getQueryData<Organization>(organizationsKeys.byId(id));
}

/**
 * Get organizations list from cache (imperative access)
 */
export function getOrganizationsListFromCache(
  queryClient: ReturnType<typeof useQueryClient>
): Organization[] | undefined {
  return queryClient.getQueryData<Organization[]>(organizationsKeys.list());
}

/**
 * Set organization in cache (for socket updates)
 */
export function setOrganizationInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  organization: Organization
): void {
  // Update individual cache
  queryClient.setQueryData(organizationsKeys.byId(organization._id), organization);

  // Update list cache
  queryClient.setQueryData<Organization[]>(organizationsKeys.list(), function (oldList) {
    if (oldList == null) {
      return [organization];
    }
    // Check if organization already exists
    const exists = oldList.some(function (org) {
      return org._id === organization._id;
    });
    if (exists) {
      return oldList.map(function (org) {
        if (org._id === organization._id) {
          return organization;
        }
        return org;
      });
    }
    return [...oldList, organization];
  });
}

/**
 * Remove organization from cache (for socket updates)
 */
export function removeOrganizationFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string
): void {
  // Remove from individual cache
  queryClient.removeQueries({ queryKey: organizationsKeys.byId(organizationId) });

  // Remove from list cache
  queryClient.setQueryData<Organization[]>(organizationsKeys.list(), function (oldList) {
    if (oldList == null) {
      return oldList;
    }
    return oldList.filter(function (org) {
      return org._id !== organizationId;
    });
  });
}

/**
 * Invalidate all organization queries
 */
export function invalidateOrganizations(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
}

/**
 * Invalidate a specific organization query
 */
export function invalidateOrganizationById(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): void {
  void queryClient.invalidateQueries({ queryKey: organizationsKeys.byId(id) });
}

/**
 * Prefetch organizations list
 */
export async function prefetchOrganizationsList(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey: organizationsKeys.list(),
    queryFn: async function () {
      const response = await getOrganizationsList();
      return response.data.organizations;
    },
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch a single organization by ID and cache it (imperative)
 * Use in beforeLoad or other non-React contexts
 */
export async function fetchOrganizationByIdImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): Promise<Organization> {
  const response = await getOrganizationById(id);

  // Cache in TanStack Query
  queryClient.setQueryData(organizationsKeys.byId(id), response.data);

  return response.data;
}

/**
 * Fetch organizations list and cache it (imperative - for use outside React components)
 * This is useful for router beforeLoad and initialization logic.
 * Also seeds byOrgId cache entries for each org so the sidebar's
 * useOrganizationUsersByUserId hook has data immediately without waiting
 * for a route-level loader.
 */
export async function fetchOrganizationsListImperative(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<Organization[]> {
  const response = await getOrganizationsList();

  // Cache organizations in TanStack Query
  queryClient.setQueryData(organizationsKeys.list(), response.data.organizations);

  // Also cache individual organizations by ID
  for (const org of response.data.organizations) {
    queryClient.setQueryData(organizationsKeys.byId(org._id), org);
  }

  // Seed byOrgId cache so sidebar hooks (useOrganizationUsersByUserId) have
  // data at boot without waiting for a route-level loader.
  // The route loader will overwrite these entries with fresh data when stale.
  if (response.data.organizationUsers != null && response.data.organizationUsers.length > 0) {
    const orgUsersGrouped: Record<string, typeof response.data.organizationUsers> = {};
    for (const orgUser of response.data.organizationUsers) {
      const orgId = orgUser.organizationId;
      if (orgUsersGrouped[orgId] == null) {
        orgUsersGrouped[orgId] = [];
      }
      orgUsersGrouped[orgId].push(orgUser);
    }
    for (const [orgId, orgUsers] of Object.entries(orgUsersGrouped)) {
      queryClient.setQueryData(organizationUsersKeys.byOrgId(orgId), orgUsers);
    }
  }

  return response.data.organizations;
}
